import React, { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDay as faRegularCalendarDay,
  faClone as faRegularClone,
  faCogs as faRegularCogs,
  faFilter as faRegularFilter,
  faSpinner as faRegularSpinner,
} from "@fortawesome/pro-regular-svg-icons";
import {
  faCircleChevronLeft,
  faCircleChevronRight,
} from "@fortawesome/pro-solid-svg-icons";
import DropDown from "components/shared/DropDown";
import { isEmpty } from "lodash";
import { CalendarFilterDropdown } from "components/appointments/toolbars/ToolBar";
import { Calendar } from "../../../@types/appointments";
import { ViewType } from "components/appointments/useAppointmentView";
import formatDate from "helpers/formatDate";
import BottomSheet from "components/shared/mobile/BottomSheet";
import { useBottomSheet } from "components/shared/hooks/mobile/useBottomSheet";
import InlineDatePicker from "components/shared/mobile/InlineDatePicker";
import { addMonths, isSameMonth, subMonths } from "date-fns";

type ToolBarArgs = {
  date: string;
  setDate: (date: string) => void;
  scrollToToday?: () => void;
  isLoading: boolean;
  calendars?: Calendar[];
  excludedCalendarIds?: string[];
  toggleExcludedCalendarIds?: (calendarId: string) => void;
  setViewType?: (viewType: ViewType) => void;
  view?: ViewType;
  hideActions?: boolean;
  canCreate?: boolean;
  calendarId?: string;
};

export default function MobileAppToolBar({
  date,
  setDate,
  scrollToToday,
  isLoading,
  calendars,
  excludedCalendarIds,
  toggleExcludedCalendarIds,
  hideActions,
  canCreate,
  calendarId,
}: ToolBarArgs) {
  const { showSheet, isAnimating, toggleSheet, closeSheet } = useBottomSheet();

  // set new item button for android app
  useEffect(() => {
    function callback(event) {
      const tabName = event["detail"].tabName;
      if (tabName === "appointments" && canCreate) {
        window.bridge?.setNewItemButton(
          `/appointments/new${calendarId ? "?calendar_id=" + calendarId : ""}`,
        );
      }
    }

    document.addEventListener("nativeTabChanged", callback);

    return () => {
      document.removeEventListener("nativeTabChanged", callback);
    };
  }, []);

  useEffect(() => {
    if (canCreate) {
      window.bridge?.setNewItemButton(
        `/appointments/new${calendarId ? "?calendar_id=" + calendarId : ""}`,
      );
    }
  }, [canCreate, calendarId]);

  return (
    <>
      <div className="flex mb-5 justify-between items-center">
        {toggleExcludedCalendarIds && (
          <>
            <DropDown.Toggle
              active={!isEmpty(excludedCalendarIds)}
              title={I18n.t("js.calendars.overview.filter_calendars")}
              disabled={!calendars || calendars.length === 0}
              icon={faRegularFilter}
            />
            <CalendarFilterDropdown
              calendars={calendars || []}
              excludedCalendarIds={excludedCalendarIds || []}
              toggleExcludedCalendarIds={toggleExcludedCalendarIds}
            />
          </>
        )}
        <div className="flex text-center">
          <button
            className="btn btn-mobile"
            onClick={() => setDate(subMonths(new Date(date), 1).toString())}
            title={I18n.t("js.calendars.overview.last_month")}
          >
            <FontAwesomeIcon icon={faCircleChevronLeft} size="xl" />
          </button>
          <button
            className="btn btn-light btn-mobile datepicker-button max-w-full grid grid-cols-[15px,auto,0.5fr] gap-1 items-center text-base"
            title={I18n.t("js.calendars.overview.current_month")}
            onClick={(e) => {
              e.preventDefault();
              toggleSheet();
            }}
          >
            {isLoading ? (
              <FontAwesomeIcon icon={faRegularSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faRegularCalendarDay} className="mr-1" />
            )}
            <span className="truncate">
              {formatDate(new Date(date), "LLLL")}
            </span>
            <span className="no-wrap">
              {formatDate(new Date(date), "yyyy")}
            </span>
          </button>
          <button
            className="btn btn-mobile"
            onClick={() => setDate(addMonths(new Date(date), 1).toString())}
            title={I18n.t("js.calendars.overview.next_month")}
          >
            <FontAwesomeIcon icon={faCircleChevronRight} size="xl" />
          </button>
        </div>

        <div className="">
          {hideActions || (
            <>
              <DropDown.Toggle />
              <DropDown className="dropdown-menu context-menu">
                <DropDown.Item
                  url="/calendars/manage"
                  icon={faRegularCogs}
                  text={I18n.t(
                    "js.calendars.overview.headline.manage_calendars",
                  )}
                />
                <DropDown.Item
                  url="/calendars/subscribe"
                  icon={faRegularCalendarDay}
                  text={I18n.t(
                    "js.calendars.overview.headline.calendar_subscribe",
                  )}
                />
                <DropDown.Item
                  onSelect={() => navigator.clipboard.writeText(location.href)}
                  icon={faRegularClone}
                  text={I18n.t("js.calendars.overview.copy_url")}
                />
              </DropDown>
            </>
          )}
        </div>
      </div>
      <BottomSheet
        showSheet={showSheet}
        isAnimating={isAnimating}
        onClose={closeSheet}
      >
        <InlineDatePicker
          date={date}
          setDate={(props) => {
            setDate(props);
            closeSheet();
            if (scrollToToday && isSameMonth(new Date(props), new Date())) {
              setTimeout(() => scrollToToday(), 300);
            }
          }}
        />
      </BottomSheet>
    </>
  );
}
